import { alpha, createTheme, type Theme } from '@mui/material/styles';

export function makeTheme(mode: 'light' | 'dark'): Theme {
  const dark = mode === 'dark';
  const primary = dark ? '#3987e5' : '#2a78d6';
  const theme = createTheme({
    palette: {
      mode,
      primary: { main: primary },
      success: { main: dark ? '#2fb36b' : '#1e9e5a' },
      warning: { main: dark ? '#f0a020' : '#e08a00' },
      error: { main: dark ? '#e66767' : '#d93636' },
      info: { main: primary },
      background: dark ? { default: '#111214', paper: '#1a1b1e' } : { default: '#f5f6f8', paper: '#ffffff' },
      divider: dark ? 'rgba(255,255,255,0.09)' : 'rgba(15,20,30,0.09)',
      text: dark ? { primary: '#ecedef', secondary: '#a5a9b1' } : { primary: '#14171c', secondary: '#5b616b' },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      h4: { fontWeight: 650, fontSize: '1.6rem', letterSpacing: '-0.01em' },
      h5: { fontWeight: 650, fontSize: '1.3rem' },
      h6: { fontWeight: 600, fontSize: '1.05rem' },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
      overline: { fontWeight: 600, letterSpacing: '0.06em' },
    },
    components: {
      MuiButton: { defaultProps: { disableElevation: true } },
      // gap instead of child margins: keeps nested Grid containers' negative margins intact
      MuiStack: { defaultProps: { useFlexGap: true } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: { defaultProps: { variant: 'outlined' } },
      MuiTableCell: {
        styleOverrides: {
          root: { paddingTop: 8, paddingBottom: 8, whiteSpace: 'nowrap' },
          head: { fontWeight: 600, fontSize: '0.75rem', color: dark ? '#a5a9b1' : '#5b616b', textTransform: 'uppercase', letterSpacing: '0.04em' },
        },
      },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } } },
      MuiTabs: { styleOverrides: { root: { minHeight: 44 } } },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: 8,
            '&.active': { backgroundColor: alpha(t.palette.primary.main, 0.12), color: t.palette.primary.main },
            '&.active .MuiListItemIcon-root': { color: t.palette.primary.main },
          }),
        },
      },
    },
  });
  return theme;
}

export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';
